import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import AddToMailingListModal from "./AddToMailingListModal";

// Admin-only roster for the event-schedule email service.
// Source: socialUsers rows where emailApproved=true. The point of this
// tab is visibility — "who is the Mon/Thu blast actually going to" —
// so we can decide whether to keep, throttle, or retire that pipeline.

const GRAPHQL_URL =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_GRAPHQL_API_URL) ||
  "https://nkeefgsaz2.execute-api.us-east-1.amazonaws.com/prod/graphql";

// The schedule-email sender's single-recipient mode (same endpoint the
// self-test curl hits): POST {email, firstName, lastName} sends the
// current schedule email to just that person.
const EMAIL_EVENTS_URL =
  "https://tn36precki.execute-api.us-east-1.amazonaws.com/default/socialEmailEvents";

const RECIPIENTS_QUERY = `
  query SocialEmailRecipients {
    socialEmailRecipients {
      userId
      email
      firstName
      lastName
      name
      unsubscribed
      unsubscribedAt
      joinDate
    }
  }
`;

const MailingListTab = () => {
  const { getAuthHeaders } = useAuth();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showUnsubscribed, setShowUnsubscribed] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [sendingTo, setSendingTo] = useState(null);

  // Send the current schedule email to ONE person, on demand. Uses
  // the sender's existing single-recipient POST mode, so what they
  // get is exactly the real Mon/Thu email as of right now.
  const sendToOne = useCallback(async (r) => {
    const display =
      [r.firstName, r.lastName].filter(Boolean).join(" ") || r.name || r.email;
    const warn = r.unsubscribed
      ? `\n\n⚠️ ${display} is marked UNSUBSCRIBED — this manual send ignores that.`
      : "";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Send the current schedule email to ${r.email}?${warn}`)) {
      return;
    }
    setSendingTo(r.userId);
    setActionMessage("");
    try {
      const nameParts = String(r.name || "").trim().split(/\s+/);
      const resp = await fetch(EMAIL_EVENTS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: r.email,
          // Joi on the sender requires both names; fall back through
          // the split display name so legacy rows still send.
          firstName: r.firstName || nameParts[0] || "Member",
          lastName: r.lastName || nameParts.slice(1).join(" ") || "-",
        }),
      });
      const body = await resp.json().catch(() => null);
      if (body && Array.isArray(body.sent) && body.sent.includes(r.email)) {
        setActionMessage(`📧 Schedule email sent to ${r.email}.`);
      } else {
        const failure =
          body && body.failed && body.failed[0] && body.failed[0].error;
        setActionMessage(
          `⚠️ Send to ${r.email} failed${failure ? `: ${failure}` : ""}`
        );
      }
    } catch (err) {
      setActionMessage(`⚠️ Send to ${r.email} failed: ${err.message}`);
    } finally {
      setSendingTo(null);
    }
  }, []);

  // Thin helpers passed into the modal so it doesn't need its own
  // graphql client — keeps the auth header path identical to the
  // existing recipient query above.
  const executeQuery = useCallback(
    async (query, variables) => {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      const resp = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ query, variables }),
      });
      const body = await resp.json();
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      if (body.errors && body.errors.length) {
        throw new Error(body.errors.map((e) => e.message).join("; "));
      }
      return body.data || {};
    },
    [getAuthHeaders]
  );

  const executeMutation = useCallback(
    async (mutation, variables) => executeQuery(mutation, variables),
    [executeQuery]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      };
      const resp = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ query: RECIPIENTS_QUERY }),
      });
      const body = await resp.json();
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      if (body.errors && body.errors.length) {
        throw new Error(body.errors.map((e) => e.message).join("; "));
      }
      setRecipients(body.data?.socialEmailRecipients || []);
    } catch (err) {
      setError(err.message || "Failed to load mailing list");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recipients.filter((r) => {
      if (!showUnsubscribed && r.unsubscribed) return false;
      if (!q) return true;
      const hay = [r.email, r.firstName, r.lastName, r.name]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [recipients, search, showUnsubscribed]);

  const counts = useMemo(() => {
    const total = recipients.length;
    const unsub = recipients.filter((r) => r.unsubscribed).length;
    return { total, unsub, active: total - unsub };
  }, [recipients]);

  return (
    <div style={{ padding: "1rem" }}>
      <div style={{ marginBottom: "1rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            marginBottom: "0.25rem",
          }}
        >
          <h2 style={{ margin: 0 }}>Mailing List</h2>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            style={{
              padding: "8px 14px",
              background: "#1f6feb",
              color: "#fff",
              border: "1px solid #1f6feb",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "13px",
              fontFamily: "inherit",
            }}
          >
            + Add to list
          </button>
        </div>
        <p style={{ color: "#555", marginTop: 0, marginBottom: "0.75rem" }}>
          Who the event-schedule email is actually going to. Source:
          socialUsers rows with <code>emailApproved=true</code>. The sender
          (backend/services/email-events) runs on Mon/Thu and skips anyone
          marked unsubscribed.
        </p>
        {actionMessage && (
          <div
            style={{
              background: "#eef6ff",
              color: "#1f3554",
              border: "1px solid #c5d9f1",
              padding: "6px 10px",
              borderRadius: "4px",
              fontSize: "13px",
              marginBottom: "8px",
            }}
          >
            {actionMessage}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            fontSize: "13px",
          }}
        >
          <span className="mailing-tile mailing-tile--active">
            <strong>{counts.active}</strong> active
          </span>
          <span className="mailing-tile mailing-tile--unsub">
            <strong>{counts.unsub}</strong> unsubscribed
          </span>
          <span className="mailing-tile">
            <strong>{counts.total}</strong> total approved
          </span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          alignItems: "center",
          marginBottom: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="Search name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: "1 1 220px",
            padding: "6px 10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        />
        <label style={{ fontSize: "13px", color: "#444" }}>
          <input
            type="checkbox"
            checked={showUnsubscribed}
            onChange={(e) => setShowUnsubscribed(e.target.checked)}
            style={{ marginRight: "4px" }}
          />
          Show unsubscribed
        </label>
        <button
          onClick={load}
          disabled={loading}
          style={{
            padding: "6px 12px",
            background: "#f0f0f0",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          style={{
            background: "#fde8e8",
            border: "1px solid #f5b5b5",
            color: "#8b1f1f",
            padding: "8px 12px",
            borderRadius: "4px",
            marginBottom: "0.75rem",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <p style={{ color: "#777" }}>No recipients match.</p>
      )}

      {filtered.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "2px solid #ddd", textAlign: "left" }}>
                <th style={{ padding: "6px 8px" }}>Name</th>
                <th style={{ padding: "6px 8px" }}>Email</th>
                <th style={{ padding: "6px 8px" }}>Joined</th>
                <th style={{ padding: "6px 8px" }}>Status</th>
                <th style={{ padding: "6px 8px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const display =
                  [r.firstName, r.lastName].filter(Boolean).join(" ") ||
                  r.name ||
                  "(no name)";
                return (
                  <tr
                    key={r.userId}
                    style={{
                      borderBottom: "1px solid #eee",
                      background: r.unsubscribed ? "#fafafa" : "transparent",
                      color: r.unsubscribed ? "#888" : "#222",
                    }}
                  >
                    <td style={{ padding: "6px 8px" }}>{display}</td>
                    <td style={{ padding: "6px 8px", fontFamily: "monospace" }}>
                      {r.email}
                    </td>
                    <td style={{ padding: "6px 8px" }}>{r.joinDate || "—"}</td>
                    <td style={{ padding: "6px 8px" }}>
                      {r.unsubscribed ? (
                        <span
                          style={{
                            background: "#fde8e8",
                            color: "#8b1f1f",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            fontSize: "11px",
                            fontWeight: 600,
                          }}
                          title={
                            r.unsubscribedAt
                              ? `Unsubscribed ${r.unsubscribedAt}`
                              : "Marked emailUnsubscribed=true"
                          }
                        >
                          Unsubscribed
                        </span>
                      ) : (
                        <span
                          style={{
                            background: "#d6f5de",
                            color: "#1f6b2f",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            fontSize: "11px",
                            fontWeight: 600,
                          }}
                        >
                          Active
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "6px 8px" }}>
                      <button
                        type="button"
                        onClick={() => sendToOne(r)}
                        disabled={sendingTo !== null}
                        title="Send this person the current schedule email now"
                        style={{
                          padding: "4px 10px",
                          background: sendingTo === r.userId ? "#eee" : "#fff",
                          border: "1px solid #1f6feb",
                          color: "#1f6feb",
                          borderRadius: "4px",
                          fontSize: "12px",
                          cursor: sendingTo !== null ? "wait" : "pointer",
                        }}
                      >
                        {sendingTo === r.userId ? "Sending…" : "📧 Send email"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddToMailingListModal
          onClose={() => setShowAddModal(false)}
          onAdded={(updated) => {
            setShowAddModal(false);
            setActionMessage(
              `${updated.email || updated.userId} is now on the list.`
            );
            load();
          }}
          executeQuery={executeQuery}
          executeMutation={executeMutation}
        />
      )}
    </div>
  );
};

export default MailingListTab;
